import { readPortable } from "./read.js";
const platform = globalThis;
/** gzip's magic number: every gzip stream starts 1f 8b. */
export function isGzip(bytes) {
    return bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;
}
async function pipe(transform, input) {
    const writer = transform.writable.getWriter();
    // Write and read at once: a stream with a full buffer stops accepting writes
    // until someone reads, so awaiting the write first can deadlock on big files.
    const written = writer.write(input).then(() => writer.close());
    // A damaged archive fails on both sides; the read side reports it, so the
    // write side's copy of the same error must not surface as unhandled.
    written.catch(() => undefined);
    const reader = transform.readable.getReader();
    const chunks = [];
    let size = 0;
    for (;;) {
        const { done, value } = await reader.read();
        if (done)
            break;
        if (value) {
            chunks.push(value);
            size += value.length;
        }
    }
    await written;
    const out = new Uint8Array(size);
    let at = 0;
    for (const chunk of chunks) {
        out.set(chunk, at);
        at += chunk.length;
    }
    return out;
}
function transform(kind) {
    const Ctor = platform[kind];
    if (!Ctor)
        throw new Error(`This runtime has no ${kind}; .gz files need a current browser or Node 18+.`);
    return new Ctor("gzip");
}
export function gzip(bytes) {
    return pipe(transform("CompressionStream"), bytes);
}
export function gunzip(bytes) {
    return pipe(transform("DecompressionStream"), bytes);
}
/** A document as the bytes of a `.backtest.gz`. Compact JSON: gzip does the rest. */
export function gzipJson(value) {
    return gzip(new platform.TextEncoder().encode(JSON.stringify(value)));
}
/**
 * The text inside a file, whether it is plain JSON or gzipped JSON. Throws on
 * a broken gzip stream or bytes that are not UTF-8 — `readPortableBytes` turns
 * both into an ordinary read error.
 */
export async function portableText(bytes) {
    const plain = isGzip(bytes) ? await gunzip(bytes) : bytes;
    return new platform.TextDecoder("utf-8", { fatal: true }).decode(plain);
}
/**
 * Reads a file's raw bytes — `.strategy`, `.backtest`, `.backtest.gz` or a
 * legacy `.strat` — into the one shape every import works with. Never throws.
 */
export async function readPortableBytes(bytes) {
    let text;
    try {
        text = await portableText(bytes);
    }
    catch {
        return {
            ok: false,
            errors: [
                isGzip(bytes) ? "This .gz file is damaged and could not be unpacked." : "This file is not text.",
            ],
        };
    }
    let value;
    try {
        value = JSON.parse(text);
    }
    catch {
        return { ok: false, errors: ["This file is not valid JSON."] };
    }
    return readPortable(value);
}
//# sourceMappingURL=gzip.js.map